import {
  createContext,
  useContext,
  useEffect,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { Socket } from 'socket.io-client';

import {
  cleanupSocketV2ResearchAssistant,
  getSocketV2ResearchAssistant,
  useFeatureFlagSplit,
} from '@verantos-dev/app-state-base';
import { useAuth } from '@verantos-dev/core-auth';

interface ResearchAssistantSocketContextType {
  socket?: Socket;
  cleanupSocket: () => void;
}

const ResearchAssistantSocketContext = createContext<ResearchAssistantSocketContextType | null>(
  null
);

export const ResearchAssistantSocketProvider = ({ children }: PropsWithChildren) => {
  const [socket, setSocket] = useState<Socket | undefined>();
  const { user } = useAuth();
  const isAgentJsonFeature = useFeatureFlagSplit('agent_json') as boolean;
  useEffect(() => {
    const initializeSocket = async () => {
      setSocket(await getSocketV2ResearchAssistant(isAgentJsonFeature ? '/agent' : undefined));
      console.info('Connecting to research assistant socket');
    };

    initializeSocket();
  }, [user, isAgentJsonFeature]);

  const cleanupSocket = useCallback(() => {
    cleanupSocketV2ResearchAssistant();
    setSocket(undefined);
  }, []);

  return (
    <ResearchAssistantSocketContext.Provider
      value={{
        socket,
        cleanupSocket,
      }}
    >
      {children}
    </ResearchAssistantSocketContext.Provider>
  );
};

export const useResearchAssistantSocketContext = () => {
  const context = useContext(ResearchAssistantSocketContext);
  if (!context) {
    throw new Error('useResearchAssistantSocketContext must be used within SocketProvider');
  }
  return context;
};
